import React from "react";
import Image from "next/legacy/image";
import styled from "styled-components";
import Button from "../Button";
import Icon from "../Icon";
import {getTheme} from "../../shared/themeUtils";
import {useMessages} from "../../hooks/useMessages";
import {withRouter} from 'next/router';
import {useCountryLang} from "../../hooks/useCountryLang";
import {request} from "../../shared/httpInterceptor";

const Contain = styled.div`
  min-height: ${getTheme("space.7")};
  padding: ${getTheme("space.2")};
  display: flex;
  justify-content: center;
  align-items: center;
  flex-direction: column;
  color: ${getTheme("colors.secondaryFont")};
  h2 {
    font-size: ${getTheme("fontSizes.medium")};
    margin-top: ${getTheme("space.2")};
    text-align: center;
  }
`;

class ErrorBoundary extends React.Component<any, { hasError: boolean, error: any, errorInfo: any }> {
  constructor(props: any) {
    super(props);
    this.state = {hasError: false, error: null, errorInfo: null};
  }

  static getDerivedStateFromError(error: any) {
    return { hasError: true };
  }

  componentDidCatch(error: any, errorInfo: any) {
    // report error to backend
    request({
      url: `/${this.props.countryLang ? this.props.countryLang : "ch/en"}/report/error`,
      method: "POST",
      data: {
        title: error?.message || 'Unknown error',
        params: {
          errorType: error?.name || 'Error',
          url: typeof window !== 'undefined' ? window.location.href : '',
          userAgent: typeof window !== 'undefined' ? window.navigator.userAgent : '',
          timestamp: new Date().toISOString()
        },
        stack: error?.stack || '',
        componentStack: errorInfo?.componentStack || '',
        // frontendErrorID: //TODO complete this with the errorID displayed to the customer
      }
    }).then(res => {
      console.log('Error reported to server:', res.data);
    }).catch(err => {
      console.error('Failed to report error to server:', err);
    });

    console.log({ error, errorInfo });
    this.setState({
      error,
      errorInfo,
    });
  }

  componentDidUpdate(prevProps: any) {
    if (prevProps.router?.asPath !== this.props.router?.asPath) {
      this.setState({hasError: false, error: null, errorInfo: null});
    }
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="appContainer">
          <Contain>
            <Image src="/stickers/404-error.svg" width={200} height={200} alt="Error"/>
              <>
                <h2>{this.props.messages ? this.props.messages["error-a-problem-occurred"] :
                  "A problem occurred and your request could not be processed"}</h2>
                <Button
                  variant={"secondary"}
                  onClick={() => {
                    this.setState({hasError: false, error: null, errorInfo: null})
                    window.location.reload()
                  }
                  }
                >
                  <Icon variant={"medium"} className={"d-inline-block"}>refresh</Icon>
                </Button>
              </>
            {this.state.error && process.env.NODE_ENV == "development" && (
              <details style={{whiteSpace: 'pre-wrap', marginTop: '2em'}}>
                <summary>Error Details</summary>
                <p>{this.state.error.toString()}</p>
              </details>
            )}
            {this.state.error && console.log(this.state.errorInfo.componentStack)}
          </Contain>
        </div>
      );
    }

    return this.props.children;
  }
}

const ErrorBoundaryWithMessages = (props: any) => {
  const messagesQuery = useMessages();
  const messages = messagesQuery.data ? messagesQuery.data.messages : null;
  const countryLang = useCountryLang()

  return <ErrorBoundary {...props} messages={messages} countryLang={countryLang}/>;
};

export default withRouter(ErrorBoundaryWithMessages);

